// @ts-nocheck

import { colors } from '@lmc/common/constants'
import { Icon } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import Flatpickr from 'react-flatpickr' // https://github.com/coderhaoxin/react-flatpickr#usage
import Select from 'react-select' // https://react-select.com/props
import styled from 'styled-components'
import { Dot } from '../../generic'

interface TodoTimeSelectorProps {
  fields: {
    recurrence: string
    startDate: string
  }
  setFormField: (args: { key: string; value: any }) => void
  noTitle?: boolean
  testId?: string
}

export default function TodoTimeSelector({
  fields,
  setFormField,
  noTitle,
  testId
}: TodoTimeSelectorProps) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  useEffect(() => {
    if (!fields.recurrence) {
      setFormField({ key: 'recurrence', value: 'once' })
    }
    if (!fields.startDate) {
      setFormField({ key: 'startDate', value: dayjs().toISOString() })
    }
  }, []) // run once on mount

  const handleTimeChange = (newDate) => {
    const { startDate } = fields
    const d = dayjs(newDate[0])
    setFormField({
      key: 'startDate',
      value: dayjs(startDate)
        .set('hour', d.get('hour'))
        .set('minute', d.get('minute'))
        .valueOf()
    })
  }

  const handleDateChange = (newDate) => {
    const { startDate } = fields
    const d = dayjs(newDate[0])
    const startDateValue = dayjs(startDate)
    setFormField({
      key: 'startDate',
      value: d
        .set('hour', startDateValue.get('hour'))
        .set('minute', startDateValue.get('minute'))
        .valueOf()
    })
  }

  const handleRecurrenceChange = ({ value }) => {
    setFormField({ key: 'recurrence', value })
  }

  const toggleRecurring = () => {
    const value = fields.recurrence === 'once' ? 'daily' : 'once'
    handleRecurrenceChange({ value })
  }

  const renderReccuranceToggle = () => {
    const isRecurring = fields.recurrence !== 'once'
    return (
      <ToggleButton
        onClick={() => toggleRecurring()}
        data-testid={
          testId
            ? `${testId}-recurring-toggle`
            : 'todo-time-selector-recurring-toggle'
        }
      >
        <Dot
          label={
            isRecurring ? (
              <Icon name="CheckOutlined" />
            ) : (
              <Icon name="CloseOutlined" />
            )
          }
          active={isRecurring}
          color={colors.green}
        />{' '}
        Recurring?
      </ToggleButton>
    )
  }

  const renderTimePicker = () => {
    const { startDate } = fields
    if (!startDate) return null

    const currentDate = dayjs(startDate).toDate()

    const date_options = {
      dateFormat: 'd M',
      defaultDate: currentDate
    }

    const time_options = {
      enableTime: true,
      noCalendar: true,
      dateFormat: 'H:i',
      defaultDate: currentDate,
      time_24hr: true
    }

    return (
      <TimeInputContainer
        data-testid={
          testId ? `${testId}-time-picker` : 'todo-time-selector-time-picker'
        }
      >
        <div
          data-testid={
            testId ? `${testId}-date-input` : 'todo-time-selector-date-input'
          }
        >
          <StyledFlatpickr options={date_options} onChange={handleDateChange} />
        </div>
        <span
          style={{ margin: 20, fontSize: 16 }}
          data-testid={
            testId ? `${testId}-at-label` : 'todo-time-selector-at-label'
          }
        >
          at:
        </span>
        <div
          data-testid={
            testId ? `${testId}-time-input` : 'todo-time-selector-time-input'
          }
        >
          <StyledFlatpickr options={time_options} onChange={handleTimeChange} />
        </div>
      </TimeInputContainer>
    )
  }

  const renderRecurringSelector = () => {
    const recTypes = [
      { value: 'daily', label: 'Daily' },
      { value: 'weekly', label: 'Weekly' },
      { value: 'bi-weekly', label: 'Bi-Weekly' },
      { value: 'monthly', label: 'Monthly' }
    ]

    return (
      <ReccuranceSelector
        data-testid={
          testId
            ? `${testId}-recurring-selector`
            : 'todo-time-selector-recurring-selector'
        }
      >
        <div
          style={{ paddingTop: 30, paddingBottom: 30, zIndex: 1000 }}
          data-testid={
            testId
              ? `${testId}-recurring-select-container`
              : 'todo-time-selector-recurring-select-container'
          }
        >
          <Select
            classNamePrefix="lmc-select"
            value={recTypes.filter((type) => type.value === fields.recurrence)}
            options={recTypes}
            onChange={handleRecurrenceChange}
            inputProps={{
              'data-testid': testId
                ? `${testId}-recurring-select`
                : 'todo-time-selector-recurring-select'
            }}
          />
        </div>
      </ReccuranceSelector>
    )
  }

  const { recurrence } = fields

  return (
    <Container data-testid={testId || 'todo-time-selector'}>
      {!noTitle && (
        <h2
          data-testid={testId ? `${testId}-title` : 'todo-time-selector-title'}
        >
          {TITLE}
        </h2>
      )}
      <FormBody
        data-testid={
          testId ? `${testId}-form-body` : 'todo-time-selector-form-body'
        }
      >
        {renderTimePicker()}
        {renderReccuranceToggle()}
        {recurrence !== 'once' ? renderRecurringSelector() : null}
      </FormBody>
    </Container>
  )
}

const TITLE = 'When is a good time to start?'

const Container = styled.div`
  margin: 20px;
  text-align: center;
`

const FormBody = styled.div`
  margin: 40px auto 0;
  width: 80%;
`

const ReccuranceSelector = styled.div`
  margin: 0 auto;
  max-width: 420px;
  position: relative;
  top: -12px;
`

const StyledFlatpickr = styled(Flatpickr)`
  border: 1px solid ${colors.grey};
  border-radius: 4px;
  letter-spacing: 1px;
  padding: 8px 4px;
  text-align: center;
  width: 100px;
`

const TimeInputContainer = styled.div`
  padding-bottom: 30px;
`

const ToggleButton = styled.div`
  cursor: pointer;
  font-weight: bold;
  margin: 0 auto;
  position: relative;
  width: 170px;

  &:hover {
    color: ${colors.darkgrey};
    opacity: 0.9;
  }
`
